import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatUSD } from "@/lib/constants";
import { billingLabel, billingSuffix } from "@/lib/pricing";
import { hasTimeline } from "@/lib/timeline";
import { useTranslation } from "react-i18next";

export default function PackageCard({ pkg, index = 0, onSelect, compact = false, selected = false, compareToggle, compareChecked = false }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const freq = pkg.billing_frequency || "one-time";
  const bLabel = pkg.billing_label || t(`cmp.bill.${freq}`, { defaultValue: billingLabel(pkg) });
  const bSuffix = pkg.billing_suffix || t(`cmp.suf.${freq === "custom" ? "one-time" : freq}`, { defaultValue: billingSuffix(pkg) });
  const handle = () => {
    if (onSelect) return onSelect(pkg);
    navigate(`/start?pkg=${pkg.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      className={`group relative flex flex-col rounded-xl border p-6 bg-card card-soft card-hover ${
        selected ? "border-orange-400 ring-1 ring-orange-200" : pkg.popular ? "border-orange-300 ring-1 ring-orange-200" : "border-border"
      }`}
      data-testid={`package-card-${pkg.id}`}
    >
      {compareToggle && (
        <label
          className="absolute top-4 right-4 z-10 flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none"
          data-testid={`compare-toggle-${pkg.id}`}
        >
          <input
            type="checkbox"
            checked={compareChecked}
            onChange={() => compareToggle(pkg)}
            className="h-4 w-4 rounded border-slate-300 accent-orange-600 cursor-pointer"
          />
          {t("cmp.pkg.compare")}
        </label>
      )}
      {pkg.popular && (
        <Badge className="absolute -top-2.5 left-6 rounded-sm bg-orange-600 text-white hover:bg-orange-600 text-[10px] tracking-wider uppercase">
          {t("cmp.pkg.popular")}
        </Badge>
      )}
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-orange-700">{t(`catalog.cat.${pkg.category}`, { defaultValue: pkg.category })}</p>
        <h3 className="mt-2 font-heading text-2xl tracking-tight text-foreground">{pkg.id?.startsWith("inc_") ? t(`country_name.${pkg.id.slice(4)}`, { defaultValue: pkg.name }) : pkg.name}</h3>
      </div>
      <p className="mt-3 text-sm text-muted-foreground leading-relaxed min-h-[40px]">{pkg.tagline}</p>

      <div className="mt-5 rounded-sm border border-border bg-secondary/40 p-4">
        <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{bLabel}</p>
        <div className="mt-1 flex items-baseline gap-1.5">
          <span className="font-heading text-3xl tracking-tight text-foreground">{formatUSD(pkg.price)}</span>
          <span className="text-xs text-muted-foreground">{bSuffix}</span>
        </div>
        {pkg.annual_fee ? (
          <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-xs">
            <span className="text-muted-foreground">{t("cmp.pkg.annual")}</span>
            <span className="font-mono text-foreground">{formatUSD(pkg.annual_fee)}{t("cmp.perYear")}</span>
          </div>
        ) : null}
        <p className="mt-2 text-xs font-mono text-orange-700">{hasTimeline(pkg.timeline) ? t("cmp.pkg.readyIn", { t: pkg.timeline }) : t("cmp.pkg.tlAfterReview")}</p>
      </div>

      {!compact && (
        <ul className="mt-6 space-y-3 flex-1">
          {pkg.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
              <span className="grid place-items-center h-4 w-4 mt-0.5 rounded-full bg-orange-100 shrink-0">
                <Check className="h-3 w-3 text-orange-700" />
              </span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}

      <Button
        onClick={handle}
        className={`mt-7 w-full rounded-sm group/btn ${
          pkg.popular || selected
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "bg-secondary text-secondary-foreground hover:bg-secondary/70"
        }`}
        data-testid={`package-select-${pkg.id}`}
      >
        {selected ? t("cmp.pkg.selected") : onSelect ? t("cmp.pkg.choose") : t("cmp.pkg.start")}
        <ArrowUpRight className="h-4 w-4 ml-1 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
      </Button>
    </motion.div>
  );
}
